import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bot, ArrowRight, Star } from "lucide-react";
import type { User } from "@supabase/supabase-js";
import { Chrome } from "lucide-react";

import FloatingIcons from "@/components/FloatingIcons";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ProblemSolution from "@/components/ProblemSolution";
import DemoVideo from "@/components/DemoVideo";
import HowItWorks from "@/components/HowItWorks";
import Features from "@/components/Features";
import BacklinkExchange from "@/components/BacklinkExchange";
import AdditionalFeatures from "@/components/AdditionalFeatures";
import WritingExamples from "@/components/WritingExamples";
import Pricing from "@/components/Pricing";
import FAQ from "@/components/FAQ";
import SEO from "@/components/SEO";
import OptimizedImage from "@/components/OptimizedImage";
import { trackCompleteRegistration, trackViewContent } from "@/utils/metaPixel";
import { trackTwitterSignUp, trackTwitterLead } from "@/utils/twitterPixel";
import { identifyUser } from "@/utils/posthog";
import { useSignIn } from "@/hooks/useSignIn";

const Index = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { handleSignIn, signingIn } = useSignIn();

  useEffect(() => {
    let isMounted = true;
    let timeoutId: NodeJS.Timeout | null = null;

    // Check if we're returning from OAuth (has code param)
    const urlParams = new URLSearchParams(window.location.search);
    const hasOAuthCode = urlParams.has('code') || urlParams.has('access_token');
    const isOAuthCallback = hasOAuthCode;

    // Safety timeout - ensure loading is always cleared after 3 seconds max (2s for OAuth callbacks)
    // OAuth callbacks need more time as Supabase processes the callback
    timeoutId = setTimeout(() => {
      if (isMounted && loading) {
        console.warn('Index page loading timeout - forcing state resolution');
        setLoading(false);
        // If OAuth callback, redirect to dashboard (session might be processing)
        if (isOAuthCallback) {
          navigate('/dashboard', { replace: true });
        }
      }
    }, isOAuthCallback ? 2000 : 3000);

    // Track ViewContent for landing page (only once per session)
    const viewContentTracked = sessionStorage.getItem('home_page_viewcontent_tracked');
    if (!viewContentTracked) {
      trackViewContent({
        content_name: 'Home Page',
        content_category: 'Landing Page',
      });
      sessionStorage.setItem('home_page_viewcontent_tracked', 'true');
    }

    // For OAuth callbacks, poll more aggressively for session
    const checkSession = async (attempt = 0) => {
      if (!isMounted) return;
      
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Error getting session:', error);
          // If OAuth callback and no session yet, keep polling (up to 3 attempts)
          if (isOAuthCallback && attempt < 3) {
            setTimeout(() => checkSession(attempt + 1), 500);
            return;
          }
          if (timeoutId) clearTimeout(timeoutId);
          setLoading(false);
          return;
        }

        setUser(session?.user ?? null);
        if (timeoutId) clearTimeout(timeoutId);
        setLoading(false);
        
        if (session?.user) {
          // Identify user in PostHog for session tracking
          identifyUser(session.user.id, {
            email: session.user.email,
            name: session.user.user_metadata?.full_name || session.user.user_metadata?.name,
          });
          navigate('/dashboard', { replace: true });
        } else if (isOAuthCallback && attempt < 3) {
          // If OAuth callback but no session yet, poll a few more times
          setTimeout(() => checkSession(attempt + 1), 500);
        }
      } catch (error) {
        console.error('Error in getSession:', error);
        // If OAuth callback and error, keep polling (up to 3 attempts)
        if (isOAuthCallback && attempt < 3) {
          setTimeout(() => checkSession(attempt + 1), 500);
          return;
        }
        if (isMounted) {
          if (timeoutId) clearTimeout(timeoutId);
          setLoading(false);
        }
      }
    };

    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!isMounted) return;
      
      setUser(session?.user ?? null);
      
      if (event === 'SIGNED_IN' && session?.user) {
        if (timeoutId) clearTimeout(timeoutId);
        setLoading(false);
        
        // Identify user in PostHog for session tracking
        identifyUser(session.user.id, {
          email: session.user.email,
          name: session.user.user_metadata?.full_name || session.user.user_metadata?.name,
        });
        
        // Check if this is a new user (first time signup) by checking if profile exists
        // If profile doesn't exist yet, it's a new registration
        try {
          const { data: profile } = await supabase
            .from('profiles')
            .select('created_at')
            .eq('user_id', session.user.id)
            .single();
          
          // If no profile or profile was just created (within last 5 seconds), it's a new registration
          const isNewUser = !profile || (new Date().getTime() - new Date(profile.created_at).getTime()) < 5000;
          
          if (isNewUser) {
            // Track registration completion
            trackCompleteRegistration({
              content_name: 'User Registration',
              content_category: 'Account Creation',
              currency: 'USD'
            });
            
            // Track Twitter/X Pixel sign-up conversion
            trackTwitterSignUp({
              email_address: session.user.email || null,
              conversion_id: session.user.id,
              status: 'completed'
            });
            
            // Track Lead as completed when user finishes sign-up
            trackTwitterLead({
              email_address: session.user.email || null,
              conversion_id: session.user.id,
              status: 'completed' // Lead completed - user has signed up
            });
          }
        } catch (profileError) {
          // Don't block navigation if profile check fails
          console.warn('Profile check error (non-critical):', profileError);
        }
        
        navigate('/dashboard');
      } else if (event === 'SIGNED_OUT') {
        if (timeoutId) clearTimeout(timeoutId);
        setLoading(false);
      }
    });
    
    return () => {
      isMounted = false;
      subscription.unsubscribe();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [navigate]);

  const scrollToSection = (sectionId: string) => {
    const isMobile = window.innerWidth < 768;
    const element = document.getElementById(sectionId);
    if (element) {
      if (isMobile) {
        // On mobile, calculate precise offset accounting for header and banner
        const header = document.querySelector('header');
        const banner = Array.from(document.querySelectorAll('div')).find(
          (el) => el.classList.contains('bg-gradient-to-r') && 
                  el.classList.contains('text-white') &&
                  el.classList.contains('static')
        );
        
        let mobileOffset = 0;
        if (header) {
          mobileOffset += header.offsetHeight;
        }
        if (banner) {
          mobileOffset += banner.offsetHeight;
        }
        mobileOffset += 10; // Small padding
        
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - mobileOffset;
        
        requestAnimationFrame(() => {
          window.scrollTo({
            top: Math.max(0, offsetPosition),
            behavior: 'smooth'
          });
        });
      } else {
        const headerOffset = 100;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background relative">
      <SEO
        title="Seoro - Grow Organic Traffic Automatically"
        description="Grow organic traffic automatically with Seoro. Generate 30 SEO-optimized articles monthly, get 10 premium backlinks (DA 70+), and track real performance—all in one platform. Stop juggling SEO tools. Start ranking."
        keywords="SEO content automation, AI content generation, premium backlinks, SEO tools, content marketing, organic traffic growth, SEO platform, automated SEO, content creation, backlink building, Google Search Console, WordPress SEO, Shopify SEO, Webflow SEO, AI writing, SEO optimization"
      />
      {/* Side shadows */}
      <div className="side-shadow side-shadow-left" />
      <div className="side-shadow side-shadow-right" />

      {/* Grid background */}
      <div
        className="absolute inset-0 w-full h-full pointer-events-none z-0"
        aria-hidden="true"
        style={{
          backgroundImage: `
            linear-gradient(rgba(229, 231, 235, 0.4) 3px, transparent 3px),
            linear-gradient(90deg, rgba(229, 231, 235, 0.4) 3px, transparent 3px)
          `,
          backgroundSize: '220px 220px',
          backgroundPosition: '50% -24px',
          maskImage: 'linear-gradient(to bottom, black 0%, black 3%, rgba(0,0,0,0.8) 7%, rgba(0,0,0,0.6) 11%, rgba(0,0,0,0.3) 15%, rgba(0,0,0,0.1) 19%, transparent 23%)',
          WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black 3%, rgba(0,0,0,0.8) 7%, rgba(0,0,0,0.6) 11%, rgba(0,0,0,0.3) 15%, rgba(0,0,0,0.1) 19%, transparent 23%)'
        }}
      />

      {/* Special Offer Banner - Static on mobile, sticky on desktop */}
      <div className="static sm:fixed top-0 left-0 right-0 z-[60] w-full bg-gradient-to-r from-primary via-primary/95 to-primary text-white shadow-lg relative">
        <div className="container mx-auto px-4 py-2.5 sm:py-3">
          <div className="flex flex-row items-center justify-center gap-2 sm:gap-3 flex-wrap">
            <p className="text-xs sm:text-sm font-bold tracking-wide text-center leading-tight">
              <span className="uppercase">Limited Time:</span> <span className="text-yellow-300 font-extrabold">40% OFF</span> - Lock in <span className="text-yellow-300 font-bold">$87.90/mo</span> forever • <span className="text-yellow-300/90">Price Lock Guaranteed</span>
            </p>
            <button
              onClick={() => scrollToSection('pricing')}
              className="text-xs sm:text-sm font-semibold text-yellow-300 hover:text-yellow-200 transition-all whitespace-nowrap underline hover:no-underline flex items-center gap-0.5 flex-shrink-0 bg-white/10 hover:bg-white/20 px-2 py-1 rounded-md"
            >
              Claim Offer<span className="hidden xs:inline"> →</span>
            </button>
          </div>
        </div>
      </div>

      {/* Spacer for fixed banner - only on desktop */}
      <div className="hidden sm:block h-[48px]"></div>

      {/* Header - Sticky */}
      <Header onSignIn={handleSignIn} />

      {/* Hero Section */}
      <main className="flex flex-col items-center justify-center text-center px-4 sm:px-6 lg:px-8 relative z-10 pt-2 sm:pt-12">
        {/* Floating Icons Container */}
        <div className="absolute inset-0 max-w-7xl mx-auto pointer-events-none" aria-hidden="true">
          <FloatingIcons />
        </div>

        {/* Main Hero Content - Optimized container with balanced spacing */}
        <div className="max-w-2xl w-full mx-auto flex flex-col items-center gap-1 sm:gap-6 lg:gap-8 pt-2 sm:pt-0 pb-4 sm:py-12 lg:py-16 relative z-10 px-4 sm:px-8 lg:px-12">
          {/* Pricing Badge */}
          <Badge variant="secondary" className="text-xs px-3 py-1.5 rounded-lg bg-primary/8 text-primary/70 border-primary/20 font-medium tracking-normal shadow-xs backdrop-blur-sm opacity-70 mb-0 sm:mb-0">
            38.6k+ ChatGPT Clicks Generated
          </Badge>

          {/* Main Headline */}
          <h1 className="text-5xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-foreground leading-[1.1] tracking-tight px-2 sm:px-0 text-center -mt-1 sm:mt-0" style={{ letterSpacing: '0.001em' }}>
            <span className="block sm:inline sm:whitespace-nowrap">Grow&nbsp;</span>
            <span className="block sm:inline whitespace-nowrap">Organic&nbsp;Traffic</span>
            <br className="hidden sm:block" />
            <span className="block sm:inline text-primary font-extrabold sm:whitespace-nowrap">on&nbsp;Auto-Pilot</span>
          </h1>

          {/* Sub-headline */}
          <h2 className="break-words text-xl sm:text-xl md:text-2xl text-muted-foreground max-w-2xl mx-auto font-medium leading-relaxed tracking-wide text-center px-2 sm:px-0 mt-2 sm:mt-0">
            Get traffic and outrank competitors{' '}
            <span className="inline-flex items-center gap-2 sm:gap-2">
              with
              <OptimizedImage
                src="https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/public/images/image-KelsTEOQnq22AGUKvLMgBuS0mK4Mzh.png"
                alt="Backlinks Icon"
                width={40}
                height={40}
                priority={true}
                className="w-6 h-6 sm:w-6 sm:h-6 md:w-8 md:h-8 lg:w-10 lg:h-10 inline-block transition-all duration-300 ease-in-out hover:-rotate-8 hover:scale-110 rotate-1 translate-y-0.5 hover:shadow-md hover:shadow-primary/15"
                style={{
                  filter: 'drop-shadow(0 2px 6px rgba(59, 130, 246, 0.15))',
                  background: 'hsl(var(--primary))',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  padding: '4px',
                  boxShadow: '0 2px 4px -1px rgba(0, 0, 0, 0.06), 0 1px 2px -1px rgba(0, 0, 0, 0.04)'
                }}
              />
              <span className="font-bold text-foreground">Backlinks</span>
            </span>{' '}
            &{' '}
            <span className="inline-flex items-center gap-2 sm:gap-2">
              <span className="font-bold text-foreground">SEO-optimized content</span>
              <OptimizedImage
                src="https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/public/images/image-BjimxRD0gb4rZBjr9jbO9LYXmOZJao.png"
                alt="SEO Icon"
                width={40}
                height={40}
                priority={true}
                className="w-6 h-6 sm:w-6 sm:h-6 md:w-8 md:h-8 lg:w-10 lg:h-10 inline-block transition-all duration-300 ease-in-out hover:rotate-8 hover:scale-110 -rotate-1 translate-y-0.5 hover:shadow-md hover:shadow-primary/15"
                style={{
                  filter: 'drop-shadow(0 2px 6px rgba(59, 130, 246, 0.15))',
                  background: 'hsl(var(--primary))',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  padding: '4px',
                  boxShadow: '0 2px 4px -1px rgba(0, 0, 0, 0.06), 0 1px 2px -1px rgba(0, 0, 0, 0.04)'
                }}
              />
            </span>{' '}
            while you sleep.
          </h2>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full justify-center max-w-lg mx-auto mt-2 sm:mt-4 px-4 sm:px-0">
            <Button
              size="lg"
              onClick={handleSignIn}
              disabled={signingIn || loading}
              variant="outline"
              className="rounded-full text-base sm:text-lg px-6 sm:px-10 py-4 sm:py-5 font-semibold border-2 border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-300 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl hover:scale-[1.02] min-h-[52px] sm:min-h-[60px] backdrop-blur-sm bg-background/90 hover:bg-background/95 group w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              <OptimizedImage
                src="https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/public/images/google.82c899b0.svg"
                alt="Google"
                width={24}
                height={24}
                priority={true}
                className="h-5 w-5 sm:h-6 sm:w-6 group-hover:scale-110 transition-transform duration-200"
              />
              <span className="hidden sm:inline">Join with Google</span>
              <span className="sm:hidden">Join with Google</span>
            </Button>
            <Button
              size="lg"
              onClick={handleSignIn}
              disabled={signingIn || loading}
              className="rounded-full text-base sm:text-lg px-6 sm:px-10 py-4 sm:py-5 font-semibold bg-gradient-to-r from-primary via-primary to-primary/90 text-primary-foreground hover:from-primary/95 hover:via-primary/90 hover:to-primary/85 transition-all duration-300 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl min-h-[52px] sm:min-h-[60px] relative overflow-hidden group w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-lg"
            >
                              <span className="relative z-10 font-semibold tracking-wide">Get Started Now</span>
              <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform duration-200 relative z-10" />
              <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 transform translate-x-[-100%] group-hover:translate-x-0 transition-transform duration-500"></div>
            </Button>
          </div>

          {/* Social Proof */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 p-4 sm:p-6 rounded-xl bg-muted/25 backdrop-blur-sm border border-border/25 shadow-sm opacity-85 w-full max-w-2xl mx-auto mt-4 sm:mt-6">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="flex -space-x-2">
                <OptimizedImage
                  src="https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/public/images/m2K2Wont_400x400.jpg"
                  alt="Customer avatar"
                  width={32}
                  height={32}
                  priority={true}
                  className="w-8 h-8 rounded-full border-2 border-white/70 shadow-sm object-cover"
                />
                <OptimizedImage
                  src="https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/public/images/BMTvrGgC_400x400.jpg"
                  alt="Customer avatar"
                  width={32}
                  height={32}
                  priority={true}
                  className="w-8 h-8 rounded-full border-2 border-white/70 shadow-sm object-cover"
                />
                <OptimizedImage
                  src="https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/public/images/avatar-4.jpg"
                  alt="Customer avatar"
                  width={32}
                  height={32}
                  priority={true}
                  className="w-8 h-8 rounded-full border-2 border-white/70 shadow-sm object-cover"
                />
                <OptimizedImage
                  src="https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/public/images/avatar-3.jpg"
                  alt="Customer avatar"
                  width={32}
                  height={32}
                  priority={true}
                  className="w-8 h-8 rounded-full border-2 border-white/70 shadow-sm object-cover"
                />
              </div>
              <div className="flex flex-col items-start gap-2">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-yellow-400/80 text-yellow-400/80" />
                  ))}
                </div>
                <span className="text-sm text-muted-foreground font-medium tracking-wide">5k+ Articles Created</span>
              </div>
            </div>
            <div className="flex flex-col items-center sm:items-start gap-1">
              <div className="text-sm font-semibold text-foreground tracking-wide text-center sm:text-left">30-Day Money Back Guarantee</div>
              <div className="text-xs text-muted-foreground font-medium tracking-normal text-center sm:text-left">Cancel anytime, keep all your content</div>
            </div>
          </div>
        </div>

        {/* AS SEEN ON - Hidden for now, can be re-enabled by removing 'hidden' class */}
        <div className="hidden relative z-10 w-full flex flex-col items-center -mt-4 sm:-mt-6 mb-6 sm:mb-8 px-4 sm:px-0">
          <div className="flex items-center gap-4 sm:gap-6 bg-muted/20 px-6 py-3 rounded-full backdrop-blur-sm border border-border/30">
            <span className="text-sm text-muted-foreground font-semibold tracking-wider uppercase">AS SEEN ON</span>
            <img 
              src="https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/public/images/Benzinga%20Logo_pitchdeck.svg" 
              alt="Benzinga" 
              className="h-8 w-20 sm:h-10 sm:w-24 object-contain"
            />
          </div>
        </div>
      </main>

      {/* New Landing Page Sections */}
      <ProblemSolution onSignIn={handleSignIn} />
      <DemoVideo />
      <HowItWorks onSignIn={handleSignIn} />
      <Features onSignIn={handleSignIn} />
      <BacklinkExchange onSignIn={handleSignIn} />
      <AdditionalFeatures onSignIn={handleSignIn} />
      <WritingExamples onSignIn={handleSignIn} />
      <Pricing onSignIn={handleSignIn} />
      <FAQ onSignIn={handleSignIn} />

      {/* Footer */}
      <Footer onSignIn={handleSignIn} />
    </div>
  );
};

export default Index;